import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Cpu, Sparkles, Crown, LineChart, Wallet, Swords } from "lucide-react";
import aelyraImg from "@/assets/instructor-aelyra.png";
import caelionImg from "@/assets/instructor-caelion.png";
import vaelorImg from "@/assets/instructor-vaelor.png";
import type { AnyValue } from "@/lib/rowTypes";

export type InstructorOpponentId = "aelyra" | "caelion" | "vaelor";

export interface InstructorOpponent {
  id: InstructorOpponentId;
  name: string;
  role: string;
  pillar: "Finance" | "Economics" | "Entrepreneurship";
  description: string;
  difficultyFeel: string;
  topics: string[];
  image: string;
  /** Course to drop the student into when they pick "Challenge". */
  practiceCourse: string;
  /** Visual theme classes. */
  accent: string;        // text + icon
  ring: string;          // border
  glow: string;          // background blur tint
  badgeBg: string;       // pillar badge bg
  PillarIcon: AnyValue;
}

export const INSTRUCTOR_OPPONENTS: InstructorOpponent[] = [
  {
    id: "aelyra",
    name: "Aelyra Solari",
    role: "Finance Instructor",
    pillar: "Finance",
    description:
      "Practice money decisions, budgeting, saving, credit, investing, and real-world financial choices with Aelyra.",
    difficultyFeel: "Calm · Balanced · Supportive",
    topics: ["Budgeting", "Saving", "Credit", "Investing Basics", "Scam Awareness"],
    image: aelyraImg,
    practiceCourse: "Saving & Budgeting",
    accent: "text-primary",
    ring: "border-primary/30",
    glow: "bg-primary/15",
    badgeBg: "bg-primary/10 text-primary border-primary/30",
    PillarIcon: Wallet,
  },
  {
    id: "caelion",
    name: "Caelion Veyr",
    role: "Economics Instructor",
    pillar: "Economics",
    description:
      "Test how well you understand markets, incentives, inflation, and the systems behind money.",
    difficultyFeel: "Analytical · Strategic · Thought-provoking",
    topics: ["Markets", "Supply & Demand", "Inflation", "Incentives", "Macroeconomics"],
    image: caelionImg,
    practiceCourse: "Economic Foundations",
    accent: "text-secondary",
    ring: "border-secondary/30",
    glow: "bg-secondary/15",
    badgeBg: "bg-secondary/10 text-secondary border-secondary/30",
    PillarIcon: LineChart,
  },
  {
    id: "vaelor",
    name: "Headmaster Vaelor",
    role: "Entrepreneurship Instructor",
    pillar: "Entrepreneurship",
    description:
      "Face the Headmaster to test your ability to create value, build ownership, and make long-term wealth decisions.",
    difficultyFeel: "Challenging · Wise · Mentor-like",
    topics: ["Entrepreneurship", "Ownership", "Value Creation", "Real Estate", "Legacy"],
    image: vaelorImg,
    practiceCourse: "Business Fundamentals",
    accent: "text-accent",
    ring: "border-accent/30",
    glow: "bg-accent/15",
    badgeBg: "bg-accent/10 text-accent border-accent/30",
    PillarIcon: Crown,
  },
];

function InstructorAvatar({
  inst,
  size = "lg",
}: {
  inst: InstructorOpponent;
  size?: "sm" | "lg";
}) {
  const dim = size === "lg" ? "w-20 h-20" : "w-12 h-12";
  const [failed, setFailed] = useState(false);
  return (
    <div className={cn("relative shrink-0 rounded-2xl overflow-hidden border", inst.ring, dim)}>
      <div className={cn("absolute inset-0", inst.glow)} />
      {!failed ? (
        <img
          src={inst.image}
          alt={inst.name}
          loading="lazy"
          onError={() => setFailed(true)}
          className="relative w-full h-full object-cover"
        />
      ) : (
        <div className={cn(
          "absolute inset-0 flex items-center justify-center font-heading font-bold text-foreground/80",
          size === "lg" ? "text-xl" : "text-sm",
        )}>
          {inst.name.slice(0, 2).toUpperCase()}
        </div>
      )}
    </div>
  );
}

/** Full premium card used in Challenge Center. */
export function InstructorOpponentCard({ inst }: { inst: InstructorOpponent }) {
  const navigate = useNavigate();
  const goPractice = () =>
    navigate(`/course/${encodeURIComponent(inst.practiceCourse)}`);

  return (
    <div
      className={cn(
        "glass-card relative overflow-hidden p-4 sm:p-5 border flex flex-col gap-3 transition-all hover:-translate-y-0.5",
        inst.ring,
      )}
    >
      <div className={cn("absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl pointer-events-none", inst.glow)} />

      <div className="relative flex items-start gap-3">
        <InstructorAvatar inst={inst} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <span className={cn(
              "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md border",
              inst.badgeBg,
            )}>
              <inst.PillarIcon className="w-3 h-3" />
              {inst.pillar}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border border-border bg-muted/30 px-2 py-0.5 rounded-md">
              <Cpu className="w-3 h-3" /> Computer
            </span>
          </div>
          <h4 className="font-heading font-bold text-foreground leading-tight truncate">{inst.name}</h4>
          <p className="text-xs text-muted-foreground truncate">{inst.role}</p>
        </div>
      </div>

      <p className="relative text-sm text-muted-foreground leading-snug">{inst.description}</p>

      <div className="relative flex flex-wrap gap-1.5">
        {inst.topics.slice(0, 4).map((t) => (
          <span
            key={t}
            className="text-[10px] px-2 py-0.5 rounded-md border border-border bg-muted/30 text-muted-foreground"
          >
            {t}
          </span>
        ))}
      </div>

      <p className={cn("relative text-[10px] uppercase tracking-wider font-semibold", inst.accent)}>
        {inst.difficultyFeel}
      </p>

      <div className="relative flex gap-2 mt-auto pt-1">
        <Button size="sm" className="flex-1" onClick={goPractice}>
          <Swords className="w-3.5 h-3.5 mr-1.5" /> Challenge
        </Button>
      </div>
    </div>
  );
}

/** Compact mini card used in Community as a fallback when friends are offline. */
export function InstructorOpponentMini({ inst }: { inst: InstructorOpponent }) {
  const navigate = useNavigate();
  return (
    <div className={cn("glass-card relative overflow-hidden p-3 border flex items-center gap-3", inst.ring)}>
      <div className={cn("absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl pointer-events-none", inst.glow)} />
      <InstructorAvatar inst={inst} size="sm" />
      <div className="relative min-w-0 flex-1">
        <p className="font-heading font-semibold text-foreground text-sm truncate">{inst.name}</p>
        <p className={cn("text-[10px] uppercase tracking-wider font-semibold truncate", inst.accent)}>
          {inst.pillar} · Computer
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="relative shrink-0"
        onClick={() => navigate(`/challenges?guide=${inst.id}`)}
      >
        <Swords className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

/** Full section header + 3-card grid for Challenge Center. */
export function ChallengeTheGuides() {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-foreground leading-tight">Challenge the Guides</h3>
            <p className="text-xs text-muted-foreground">Practice against the Luminar Collective whenever your friends are offline.</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {INSTRUCTOR_OPPONENTS.map((inst) => (
          <InstructorOpponentCard key={inst.id} inst={inst} />
        ))}
      </div>
    </section>
  );
}

/** Compact strip for Community. */
export function GuidesAvailableStrip() {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-accent/15 border border-accent/30 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-accent" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-foreground leading-tight">Guides Available to Challenge</h3>
          <p className="text-xs text-muted-foreground">No friends online? Practice against the Luminar Collective.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {INSTRUCTOR_OPPONENTS.map((inst) => (
          <InstructorOpponentMini key={inst.id} inst={inst} />
        ))}
      </div>
    </section>
  );
}